/**
 * Opacity for a proposal's map pins. With no proposal selected every proposal
 * is fully opaque; selecting one (via its pin or the View route button) keeps
 * that proposal solid and fades the rest so the LS can focus on it.
 *
 * @param {{id: number}} grouping - the proposal whose pin is being drawn
 * @param {?{grouping_id: number}} gmproRoute - the currently viewed route, or null
 * @return {number} 1 for full opacity, 0.5 for dimmed
 */
export const gmproPinOpacity = (grouping, gmproRoute) => {
  if (!gmproRoute) { return 1; }
  return gmproRoute.grouping_id === grouping.id ? 1 : 0.5;
};

/**
 * Deduped union of any number of address-name lists. The trips page derives the
 * company-address dropdown from this so the same office is selectable regardless
 * of which tab is active: a proposal's office on the Proposed tab, and the same
 * office on Bookings/Unassigned/Assigned once proposals are accepted or declined.
 * Falsy sources and falsy entries are skipped.
 *
 * @param {...(string[]|null|undefined)} sources - address-name lists to merge
 * @return {string[]} unique address names
 */
export const mergeAddressSources = (...sources) => {
  const merged = new Set();
  sources.forEach((source) => {
    (source || []).forEach((address) => {
      if (address) { merged.add(address); }
    });
  });
  return Array.from(merged);
};

/**
 * Enforces the invariant that a trip belonging to any group can never also
 * appear in the ungrouped list. The re-derive effect rebuilds the displayed
 * ungrouped list from a source (allTrips) that can be stale right after a
 * (re)group — still listing a trip that is now grouped — which surfaces as a
 * phantom duplicate. Filtering the ungrouped list against the fresh group maps
 * drops that trip regardless of the stale source, so no refetch is required.
 *
 * Each group map is keyed by group id; its values are arrays of trip objects.
 *
 * @param {?Array<{id: *}>} ungroupedTrips - the candidate ungrouped trips
 * @param {...(Object<string, Array<{id: *}>>|null|undefined)} groupMaps - group maps whose trips to exclude
 * @return {Array<{id: *}>} ungroupedTrips with any already-grouped trip removed
 */
export const excludeGroupedTrips = (ungroupedTrips, ...groupMaps) => {
  if (!Array.isArray(ungroupedTrips)) { return []; }

  const groupedIds = new Set();
  groupMaps.forEach((groupMap) => {
    Object.values(groupMap || {}).forEach((trips) => {
      (trips || []).forEach((trip) => {
        if (trip && trip.id != null) { groupedIds.add(trip.id); }
      });
    });
  });

  return ungroupedTrips.filter((trip) => trip && !groupedIds.has(trip.id));
};
